"use client";

import { doc, onSnapshot, getDoc, Firestore } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import type { FirestoreDataConverter } from "firebase/firestore";

// Utility to safely convert unknown errors to Error objects
const toError = (err: unknown): Error => {
  if (err instanceof Error) {
    return err;
  }
  if (typeof err === "string") {
    return new Error(err);
  }
  return new Error(`Unknown error: ${String(err)}`);
};

type UseFetchDocProps<T> = {
  db: Firestore;
  path: string;
  converter: FirestoreDataConverter<T>;
  realtime?: boolean;
};

export const useFetchDoc = <T>({
  db,
  path,
  converter,
  realtime = false,
}: UseFetchDocProps<T>) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchedRef = useRef(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isSettingUpRef = useRef(false);

  useEffect(() => {
    // Check if path ends with empty string (indicating missing document ID)
    if (
      !path ||
      path.trim() === "" ||
      path.endsWith("/") ||
      path.split("/").some((segment) => segment === "")
    ) {
      setData(null);
      setLoading(false);
      setError(new Error(`Invalid document path: ${path}`));
      return;
    }

    if (!realtime && fetchedRef.current) {
      return;
    }

    if (realtime) {
      // Check if listener already exists or setup is in progress
      // This prevents React Strict Mode double invocation from setting up duplicate listeners
      // IMPORTANT: Check unsubscribeRef first - it persists even through cleanup in Strict Mode's immediate cleanup cycle
      if (unsubscribeRef.current) {
        return;
      }
      
      if (isSettingUpRef.current) {
        return;
      }

      // Mark as setting up BEFORE calling onSnapshot
      isSettingUpRef.current = true;

      // Defer setup to next microtask to allow React Strict Mode cleanup to complete
      // This prevents race conditions when Strict Mode calls cleanup synchronously
      const setupTimeout = setTimeout(() => {
        // Double-check we're still supposed to set up (cleanup might have run)
        if (unsubscribeRef.current) {
          // Listener already exists, skip (shouldn't happen, but safety check)
          isSettingUpRef.current = false;
          return;
        }
        
        if (!isSettingUpRef.current) {
          // Setup was cancelled (cleanup ran), skip
          return;
        }

        // Let React's useEffect cleanup handle listener teardown naturally
        // This avoids race conditions from manual cleanup/setup cycles
        const docRef = doc(db, path).withConverter(converter);
        const unsubscribe = onSnapshot(
          docRef,
          (snapshot) => {
            if (snapshot.exists()) {
              setData(snapshot.data());
            } else {
              setData(null);
              setError(new Error(`Document not found. Doc path: ${path} `));
            }
            setLoading(false);
          },
          (err) => {
            setError(toError(err));
            setLoading(false);
          },
        );
        
        // Store unsubscribe and mark setup as complete
        unsubscribeRef.current = unsubscribe;
        isSettingUpRef.current = false;
      }, 0);

      return () => {
        clearTimeout(setupTimeout);
        // Reset flags to prevent deferred setup from proceeding
        isSettingUpRef.current = false;
        // Reset refs before unsubscribing to allow next setup
        const fn = unsubscribeRef.current;
        unsubscribeRef.current = null;
        if (fn) {
          fn();
        }
      };
    } else {
      const doFetchDoc = async () => {
        try {
          const docRef = doc(db, path).withConverter(converter);
          const docSnapshot = await getDoc(docRef);
          if (docSnapshot.exists()) {
            setData(docSnapshot.data());
          } else {
            setError(new Error(`Document not found. Doc path: ${path} `));
          }
          fetchedRef.current = true;
        } catch (err) {
          setError(toError(err));
        } finally {
          setLoading(false);
        }
      };

      doFetchDoc();
      return () => {};
    }
  }, [db, path, converter, realtime]);

  return { data, loading, error };
};

